import { z } from 'zod';
import { sha256Hex } from '../digest';
import { RELEASE_STAGES } from './release';

/**
 * A backup that somebody actually checked.
 *
 * ## Why a string was not enough
 *
 * `--backup s3://something` reassures everybody and restores nothing. It says
 * a person believed a backup existed; it does not say the file is there, that
 * it is the right file, that it is not truncated, or that it can be read back
 * into a database. The one moment that matters is the moment it cannot.
 *
 * So the argument is a MANIFEST: a record of a dump that exists, whose
 * SHA-256 was computed over its bytes, and that was RESTORED into a throwaway
 * database and found to contain what it should.
 *
 * ## What it must never carry
 *
 * No password, no token, no connection URL with credentials, no secret of any
 * kind. It is written to disk, hashed into a plan, stored in a row and pasted
 * into a report - four places a credential must not reach. The schema below is
 * `.strict()`, so a field nobody anticipated is a refusal rather than
 * something quietly carried along.
 */

export const backupManifestSchema = z
  .object({
    /** Opaque, operator-chosen. Names the backup in a report. */
    backupId: z.string().min(1).max(128),
    /** The database it was taken FROM. Name only. */
    sourceDatabase: z.string().min(1).max(128),
    /** Host and port. Never a URL, and never credentials. */
    sourceHost: z.string().min(1).max(200),
    environment: z.string().min(1).max(32),
    /** The stage the game was in when it was taken. */
    stage: z.enum(RELEASE_STAGES),
    /**
     * The maintenance this backup belongs to.
     *
     * Not a timestamp somebody typed: the unpredictable id the database issued
     * when the doors closed. A dump from last month's maintenance cannot
     * authorise this month's reset, however recent its clock says it is.
     */
    maintenanceId: z.string().min(8).max(128),
    /** UTC, ISO-8601 with an offset. When the dump was produced. */
    createdAt: z.iso.datetime(),
    /** Where the file is. A path or an object key - not a signed URL. */
    filePath: z.string().min(1).max(1024),
    /** Bytes. Zero is an empty file, which is not a backup. */
    fileBytes: z.number().int().positive(),
    /** SHA-256 of the file's bytes, lowercase hex. */
    sha256: z.string().regex(/^[0-9a-f]{64}$/),
    /** When the RESTORE was validated. Not when the dump was taken. */
    restoreVerifiedAt: z.iso.datetime(),
    /** The throwaway database it was restored into, to prove it can be. */
    restoreDatabase: z.string().min(1).max(128),
    /** What the restored copy was found to contain. */
    restoreChecks: z
      .object({
        /** Tables present after the restore. */
        tables: z.number().int().min(1),
        accounts: z.number().int().min(0),
        ledgerEntries: z.number().int().min(0),
        storeOrders: z.number().int().min(0),
        cashPurchases: z.number().int().min(0),
        itemInstances: z.number().int().min(0),
        /**
         * How many migrations the restored copy has applied.
         *
         * A dump restored into a database at a different schema version is a
         * different database. Compared with the source, never with a constant.
         */
        migrationsApplied: z.number().int().min(0),
        /** Constraints and triggers, which is what makes the data stay true. */
        constraints: z.number().int().min(0),
        triggers: z.number().int().min(0),
      })
      .strict(),
    /**
     * SHA-256 over the per-table row counts AND content digests.
     *
     * The counts alone proved a restore had the right SHAPE and nothing about
     * its contents: a dump that came back with every row present and one Cash
     * balance different would have passed. This digest is computed over the
     * hash of every row of every public table, on both sides, and they have to
     * be the same string.
     */
    contentDigest: z.string().regex(/^[0-9a-f]{64}$/),
    /** `passed` or nothing runs. Any other word is a refusal. */
    result: z.literal('passed'),
  })
  .strict();

export type BackupManifest = z.infer<typeof backupManifestSchema>;

/** Why a manifest does not authorise a reset. Every one is fail-closed. */
export const BACKUP_REFUSALS = [
  'manifest_missing',
  'manifest_malformed',
  /** The file named by the manifest is not there. */
  'file_missing',
  /** Zero bytes, or fewer than the manifest claims. */
  'file_empty',
  /** The bytes on disk do not hash to what the manifest says. */
  'hash_mismatch',
  /** A backup of some OTHER database. */
  'database_mismatch',
  'environment_mismatch',
  /** Taken in a different release stage. */
  'stage_mismatch',
  /** Taken BEFORE maintenance started, so it may miss what happened after. */
  'taken_before_maintenance',
  /** Nobody proved it could be restored. */
  'restore_not_verified',
  /** A backup from some OTHER maintenance. */
  'maintenance_mismatch',
  /** There is no maintenance at all: the doors are still open. */
  'maintenance_not_active',
  /** The restore was proved before the doors closed. */
  'restore_before_maintenance',
  /** The manifest changed after the plan was frozen with it. */
  'manifest_changed_since_prepare',
  /** The restore was verified too long ago, or against another file. */
  'restore_stale',
] as const;
export type BackupRefusal = (typeof BACKUP_REFUSALS)[number];

/**
 * How old a restore proof may be.
 *
 * Twenty-four hours. A dump verified last month may sit on a disk that has
 * since filled, been rotated or been re-encrypted, and the proof would be
 * about a file that is no longer the file.
 */
export const RESTORE_PROOF_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export interface BackupCheckInput {
  /** The parsed manifest, or null when there was nothing to parse. */
  readonly manifest: BackupManifest | null;
  /** Whether the parse itself failed, as opposed to the file being absent. */
  readonly malformed?: boolean;
  /** What the file on disk actually is. Null when it is not there. */
  readonly file: { readonly bytes: number; readonly sha256: string } | null;
  /** The database this reset would run against. Name only. */
  readonly targetDatabase: string;
  readonly targetEnvironment: string;
  /** The stage the reset leaves. */
  readonly targetStage: string;
  /** When transition maintenance began. A backup older than this is stale. */
  readonly maintenanceSince: Date | null;
  /** The id the database issued when the doors closed. Null when they are open. */
  readonly maintenanceId?: string | null;
  /**
   * The manifest hash a run was already prepared with, when there is one.
   *
   * Apply, verify and finalize all check it: swapping the manifest after the
   * plan was frozen would mean the run points at a backup nobody agreed to.
   */
  readonly preparedManifestHash?: string | null;
  /** SHA-256 of the manifest being offered now. */
  readonly manifestHash?: string | null;
  /**
   * Is this a throwaway database? Inside one the maintenance binding is
   * checked when present and not demanded, so a rehearsal exercises the same
   * code without needing a full operational ceremony.
   */
  readonly disposable?: boolean;
  readonly now: Date;
}

/**
 * Every reason this backup does not authorise a reset.
 *
 * Exhaustive rather than short-circuiting, so an operator sees all of it at
 * once. Empty means the backup is real, current, intact, restorable and about
 * the right database - and nothing less than all five.
 */
export function backupRefusals(input: BackupCheckInput): BackupRefusal[] {
  const refusals: BackupRefusal[] = [];
  if (input.malformed) {
    return ['manifest_malformed'];
  }
  const manifest = input.manifest;
  if (!manifest) {
    return ['manifest_missing'];
  }

  if (!input.file) {
    refusals.push('file_missing');
  } else {
    if (input.file.bytes <= 0 || input.file.bytes < manifest.fileBytes) {
      // Truncated counts as empty: half a dump restores nothing.
      refusals.push('file_empty');
    }
    if (input.file.sha256 !== manifest.sha256) {
      refusals.push('hash_mismatch');
    }
  }

  if (manifest.sourceDatabase !== input.targetDatabase) {
    // Restoring somebody else's database over this one is the worst outcome
    // available, so the check is on the NAME rather than on a belief.
    refusals.push('database_mismatch');
  }
  if (manifest.environment !== input.targetEnvironment) {
    refusals.push('environment_mismatch');
  }
  if (manifest.stage !== input.targetStage) {
    refusals.push('stage_mismatch');
  }

  const createdAt = Date.parse(manifest.createdAt);
  if (input.maintenanceSince && createdAt < input.maintenanceSince.getTime()) {
    /*
     * A dump taken before the doors closed may be missing everything that
     * happened while they were still open - the last purchases, the last
     * trades - and restoring it would silently undo them.
     */
    refusals.push('taken_before_maintenance');
  }

  /*
   * The BINDING. A timestamp says when; an id says which.
   *
   * Outside a throwaway database the doors must be shut and the backup must
   * name the very maintenance that shut them - so a dump taken during an
   * earlier one, however recent, authorises nothing.
   */
  if (!input.disposable) {
    if (!input.maintenanceId) {
      refusals.push('maintenance_not_active');
    } else if (manifest.maintenanceId !== input.maintenanceId) {
      refusals.push('maintenance_mismatch');
    }
    if (!input.maintenanceSince) {
      // Never skipped silently outside a disposable database.
      refusals.push('taken_before_maintenance');
    }
  } else if (input.maintenanceId && manifest.maintenanceId !== input.maintenanceId) {
    refusals.push('maintenance_mismatch');
  }

  if (
    input.preparedManifestHash &&
    input.manifestHash &&
    input.preparedManifestHash !== input.manifestHash
  ) {
    // The run was frozen with a different backup. Swapping it now would point
    // the whole reset at something nobody agreed to.
    refusals.push('manifest_changed_since_prepare');
  }

  const verifiedAt = Date.parse(manifest.restoreVerifiedAt);
  if (!Number.isFinite(verifiedAt) || verifiedAt < createdAt) {
    // A proof that predates the file it is about is a proof about another file.
    refusals.push('restore_not_verified');
  } else if (input.now.getTime() - verifiedAt > RESTORE_PROOF_MAX_AGE_MS) {
    refusals.push('restore_stale');
  } else if (input.maintenanceSince && verifiedAt < input.maintenanceSince.getTime()) {
    // A restore proved before the doors closed is a proof about a database
    // that was still moving.
    refusals.push('restore_before_maintenance');
  }

  return refusals;
}

/**
 * What a restored copy must contain before the manifest may say `passed`.
 *
 * Structure and volume, not contents: the point is that the dump restores into
 * a working schema with the rows it claims, not that any particular player is
 * in it. Counts are compared with the SOURCE, so a restore that silently lost
 * a table or half a table is caught.
 */
/**
 * The manifest, as bytes two processes can agree on.
 *
 * Keys sorted at every level, nested object included, no whitespace. The tool
 * that prepares a run and the service that later checks the run was prepared
 * with THIS backup both hash this string, so a manifest cannot be swapped for
 * another after the plan was frozen with it.
 */
export function canonicalBackupManifest(manifest: BackupManifest): string {
  const canonical = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map(canonical);
    }
    if (value !== null && typeof value === 'object') {
      return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
          .sort(([a], [b]) => (a < b ? -1 : 1))
          .map(([key, inner]) => [key, canonical(inner)]),
      );
    }
    return value;
  };
  return JSON.stringify(canonical(manifest));
}

/** SHA-256 of the canonical manifest. What a run is bound to. */
export function backupManifestHash(manifest: BackupManifest): Promise<string> {
  return sha256Hex(canonicalBackupManifest(manifest));
}

export function restoreProblems(
  source: BackupManifest['restoreChecks'],
  restored: BackupManifest['restoreChecks'],
): string[] {
  const problems: string[] = [];
  for (const key of Object.keys(source) as (keyof BackupManifest['restoreChecks'])[]) {
    if (restored[key] !== source[key]) {
      problems.push(`${key}: expected ${source[key]}, restored ${restored[key]}`);
    }
  }
  return problems;
}

/**
 * One public table, as a number of rows and a hash of every row in it.
 *
 * The digest is order-independent by construction - each row is hashed and the
 * hashes are combined in sorted order - because a restore is free to give the
 * rows back in a different physical order and that is not a difference.
 */
export interface TableFingerprint {
  readonly rows: number;
  /** Hex. A hash of the hashes of every row. */
  readonly digest: string;
}

/**
 * Everything that differs between the source and the restored copy.
 *
 * ## Why counts were not enough
 *
 * A restore that returns every table with every row and ONE Cash balance
 * different passes a count check perfectly. So does one where two accounts'
 * items were swapped, or a VIP expiry moved by a day. Counting proves shape;
 * it proves nothing about contents, and contents are the entire reason a
 * backup exists.
 *
 * So every table is fingerprinted on both sides and the two are compared table
 * by table: a missing table, a table nobody expected, a different number of
 * rows, or the same rows with different bytes in them. Exhaustive rather than
 * short-circuiting - an operator reading this wants the whole list.
 */
export function restoreFingerprintProblems(
  source: Readonly<Record<string, TableFingerprint>>,
  restored: Readonly<Record<string, TableFingerprint>>,
): string[] {
  const problems: string[] = [];
  for (const table of Object.keys(source).sort()) {
    const before = source[table] as TableFingerprint;
    const after = restored[table];
    if (!after) {
      problems.push(`${table}: missing from the restored copy`);
      continue;
    }
    if (after.rows !== before.rows) {
      problems.push(`${table}: expected ${before.rows} rows, restored ${after.rows}`);
    }
    if (after.digest !== before.digest) {
      // The one the counts could never see.
      problems.push(`${table}: ${before.rows} rows restored, contents differ`);
    }
  }
  for (const table of Object.keys(restored).sort()) {
    if (!source[table]) {
      problems.push(`${table}: present in the restored copy and not in the source`);
    }
  }
  return problems;
}

/**
 * The whole restored copy as one string somebody can compare by eye.
 *
 * Sorted, so two processes that read the same database produce the same bytes.
 * It goes in the manifest, where a difference of one row in one table changes
 * it entirely.
 */
export function restoreDigest(
  fingerprints: Readonly<Record<string, TableFingerprint>>,
): Promise<string> {
  const canonical = Object.keys(fingerprints)
    .sort()
    .map((table) => {
      const entry = fingerprints[table] as TableFingerprint;
      return `${table}:${entry.rows}:${entry.digest}`;
    })
    .join('\n');
  return sha256Hex(canonical);
}
